import { z } from "zod";

import { applyCommentChange, commentTarget, type CommentTargetRequest } from "./comment-anchors";
import { SENTRY_TAG_ERROR_TYPE } from "./redline-failure-sentry";
import { reportMessage } from "./sentry-reporter";

const WordCommentInsertRefusal = z.object({
    code: z.literal("AccessDenied"),
    debugInfo: z.object({ errorLocation: z.literal("Range.insertComment") }),
});

const WordErrorWithCode = z.object({ code: z.string() });

const CommentInsertRetryRoute = z.enum(["same_target", "paragraph_content"]);
type CommentInsertRetryRoute = z.infer<typeof CommentInsertRetryRoute>;

export interface CommentInsertLanding {
    route: CommentInsertRetryRoute;
    landedOn: Word.Range;
}

const COMMENT_INSERT_REFUSED_ERROR_TYPE = "comment_insert_refused";
const COMMENT_INSERT_REFUSED_MESSAGE = "Word refused a comment insert";
const COMMENT_INSERT_REFUSAL_CONTEXT_KEY = "comment_insert_refusal";
const COMMENT_INSERT_REFUSALS_OF_THE_RUN_CONTEXT_KEY = "comment_insert_refusals_of_the_run";
const SENTRY_TAG_COMMENT_INSERT_OUTCOME = "comment_insert_outcome";
const REFUSED_EVERYWHERE_OUTCOME = "refused_everywhere";
const RETRY_ERRORED_OUTCOME = "retry_errored";
const UNKNOWN_WORD_ERROR_CODE = "unknown";
const SENTRY_TAG_DOCUMENT_PAGE_COUNT = "document_page_count";
const UNKNOWN_PAGE_COUNT = "unknown";
const PROBE_COMMENT_TEXT = "Dioptra comment insert probe";

interface CommentProbeErrored {
    errorCode: string;
}

interface CommentProbeLeftInDocument {
    cleanupErrorCode: string;
}

type CommentProbeOutcome = "accepted" | "refused" | CommentProbeErrored | CommentProbeLeftInDocument;

interface CommentProbeElsewhere {
    bodyStart: CommentProbeOutcome;
    firstParagraph: CommentProbeOutcome;
    wholeBody: CommentProbeOutcome;
}

interface WordStateReadSucceeded {
    read: "succeeded";
    wordBuild: string;
    changeTrackingMode: Word.Document["changeTrackingMode"];
    protectionType: Word.Document["protectionType"] | null;
    trackedChangeCountInBookmarkedRange: number | null;
    bookmarkedRangeInsideContentControl: boolean;
    enclosingContentControlCannotEdit: boolean | null;
    bookmarkedRangeInsideTable: boolean;
}

interface WordReadFailed {
    read: "failed";
    errorCode: string;
}

type CommentInsertRefusalWordState = WordStateReadSucceeded | WordReadFailed;

interface DocumentSizeReadSucceeded {
    read: "succeeded";
    pageCount: number | null;
    sectionCount: number;
    sectionsWithPrimaryFooterText: number;
    contentControlCount: number;
}

type CommentInsertRefusalDocumentSize = DocumentSizeReadSucceeded | WordReadFailed;

interface CommentInsertRefusalReport {
    landedRoute: CommentInsertRetryRoute | null;
    retryErrorCode: string | null;
    wordState: CommentInsertRefusalWordState;
    documentSize: CommentInsertRefusalDocumentSize;
    probeElsewhere: CommentProbeElsewhere | null;
}

const retryTargetOf: Record<CommentInsertRetryRoute, (request: CommentTargetRequest) => Promise<Word.Range>> = {
    same_target: async (request) => (await commentTarget(request)).range,
    paragraph_content: async (request) => request.bookmarkedRange.paragraphs.getFirst().getRange("Content"),
};

export function isWordCommentInsertRefusal(error: unknown): boolean {
    return WordCommentInsertRefusal.safeParse(error).success;
}

export async function retryRefusedCommentInsert(
    context: Word.RequestContext,
    request: CommentTargetRequest,
    commentText: string,
    refusal: unknown,
): Promise<CommentInsertLanding> {
    const wordState = await readOrItsFailure(() => readWordStateAround(context, request.bookmarkedRange));
    const documentSize = await readOrItsFailure(() => readDocumentSize(context));
    const landing = await firstRouteWordAccepts(request, commentText).catch((retryError: unknown) => {
        reportCommentInsertRefusal({ landedRoute: null, retryErrorCode: wordErrorCodeOf(retryError), wordState, documentSize, probeElsewhere: null });
        throw retryError;
    });
    const probeElsewhere = landing === null ? await probeCommentInsertElsewhere(context) : null;
    reportCommentInsertRefusal({ landedRoute: landing?.route ?? null, retryErrorCode: null, wordState, documentSize, probeElsewhere });
    if (landing === null) throw refusal;
    return landing;
}

async function probeCommentInsertElsewhere(context: Word.RequestContext): Promise<CommentProbeElsewhere> {
    const body = context.document.body;
    return {
        bodyStart: await commentProbeOutcomeOn(context, body.getRange("Start")),
        firstParagraph: await commentProbeOutcomeOn(context, body.paragraphs.getFirst().getRange("Content")),
        wholeBody: await commentProbeOutcomeOn(context, body.getRange("Whole")),
    };
}

async function commentProbeOutcomeOn(context: Word.RequestContext, probeRange: Word.Range): Promise<CommentProbeOutcome> {
    const probeComment = probeRange.insertComment(PROBE_COMMENT_TEXT);
    try {
        await context.sync();
    } catch (error) {
        return isWordCommentInsertRefusal(error) ? "refused" : { errorCode: wordErrorCodeOf(error) };
    }
    return deleteAcceptedProbeComment(context, probeComment);
}

async function deleteAcceptedProbeComment(context: Word.RequestContext, probeComment: Word.Comment): Promise<CommentProbeOutcome> {
    try {
        probeComment.delete();
        await context.sync();
        return "accepted";
    } catch (error) {
        return { cleanupErrorCode: wordErrorCodeOf(error) };
    }
}

async function firstRouteWordAccepts(request: CommentTargetRequest, commentText: string): Promise<CommentInsertLanding | null> {
    for (const route of CommentInsertRetryRoute.options) {
        const landedOn = await rangeWordAcceptsCommentOn(route, request, commentText);
        if (landedOn !== null) return { route, landedOn };
    }
    return null;
}

async function rangeWordAcceptsCommentOn(route: CommentInsertRetryRoute, request: CommentTargetRequest, commentText: string): Promise<Word.Range | null> {
    const retryTarget = await retryTargetOf[route](request);
    try {
        await applyCommentChange(retryTarget, commentText);
        return retryTarget;
    } catch (error) {
        if (isWordCommentInsertRefusal(error)) return null;
        throw error;
    }
}

async function readOrItsFailure<Read>(read: () => Promise<Read>): Promise<Read | WordReadFailed> {
    try {
        return await read();
    } catch (error) {
        return { read: "failed", errorCode: wordErrorCodeOf(error) };
    }
}

async function readWordStateAround(context: Word.RequestContext, bookmarkedRange: Word.Range): Promise<WordStateReadSucceeded> {
    const protectionTypeReadable = Office.context.requirements.isSetSupported("WordApiDesktop", "1.4");
    const trackedChangesReadable = Office.context.requirements.isSetSupported("WordApi", "1.6");
    context.document.load(protectionTypeReadable ? "changeTrackingMode,protectionType" : "changeTrackingMode");
    const trackedChanges = trackedChangesReadable ? bookmarkedRange.getTrackedChanges() : null;
    trackedChanges?.load("items");
    const enclosingContentControl = bookmarkedRange.parentContentControlOrNullObject;
    enclosingContentControl.load("isNullObject");
    const enclosingTable = bookmarkedRange.parentTableOrNullObject;
    enclosingTable.load("isNullObject");
    await context.sync();

    return {
        read: "succeeded",
        wordBuild: Office.context.diagnostics.version,
        changeTrackingMode: context.document.changeTrackingMode,
        protectionType: protectionTypeReadable ? context.document.protectionType : null,
        trackedChangeCountInBookmarkedRange: trackedChanges === null ? null : trackedChanges.items.length,
        bookmarkedRangeInsideContentControl: !enclosingContentControl.isNullObject,
        enclosingContentControlCannotEdit: await cannotEditOf(context, enclosingContentControl),
        bookmarkedRangeInsideTable: !enclosingTable.isNullObject,
    };
}

async function cannotEditOf(context: Word.RequestContext, enclosingContentControl: Word.ContentControl): Promise<boolean | null> {
    if (enclosingContentControl.isNullObject) return null;
    enclosingContentControl.load("cannotEdit");
    await context.sync();
    return enclosingContentControl.cannotEdit;
}

async function readDocumentSize(context: Word.RequestContext): Promise<DocumentSizeReadSucceeded> {
    const pagesReadable = Office.context.requirements.isSetSupported("WordApiDesktop", "1.2");
    const pages = pagesReadable ? context.document.activeWindow.activePane.pages : null;
    pages?.load("items/index");
    const sections = context.document.sections;
    sections.load("items");
    const contentControls = context.document.contentControls;
    contentControls.load("items/id");
    await context.sync();

    const primaryFooters = sections.items.map((section) => section.getFooter("Primary"));
    primaryFooters.forEach((footer) => footer.load("text"));
    await context.sync();

    return {
        read: "succeeded",
        pageCount: pages === null ? null : pages.items.length,
        sectionCount: sections.items.length,
        sectionsWithPrimaryFooterText: primaryFooters.filter((footer) => footer.text.trim().length > 0).length,
        contentControlCount: contentControls.items.length,
    };
}

function pageCountTagOf(documentSize: CommentInsertRefusalDocumentSize): string {
    if (documentSize.read === "failed" || documentSize.pageCount === null) return UNKNOWN_PAGE_COUNT;
    return String(documentSize.pageCount);
}

function wordErrorCodeOf(error: unknown): string {
    const parsed = WordErrorWithCode.safeParse(error);
    return parsed.success ? parsed.data.code : UNKNOWN_WORD_ERROR_CODE;
}

function outcomeOf(report: CommentInsertRefusalReport): string {
    if (report.landedRoute !== null) return report.landedRoute;
    return report.retryErrorCode === null ? REFUSED_EVERYWHERE_OUTCOME : RETRY_ERRORED_OUTCOME;
}

let refusalReportsOfTheRun: CommentInsertRefusalReport[] | null = null;

export async function reportCommentInsertRefusalsOncePerOutcome<T>(writes: () => Promise<T>): Promise<T> {
    const refusalReportsOfAnOuterRun = refusalReportsOfTheRun;
    refusalReportsOfTheRun = [];
    try {
        return await writes();
    } finally {
        const refusalReportsOfThisRun = refusalReportsOfTheRun;
        refusalReportsOfTheRun = refusalReportsOfAnOuterRun;
        sendOneRefusalReportPerOutcome(refusalReportsOfThisRun);
    }
}

function sendOneRefusalReportPerOutcome(refusalReports: CommentInsertRefusalReport[]): void {
    const refusalReportsByOutcome = new Map<string, CommentInsertRefusalReport[]>();
    for (const refusalReport of refusalReports) {
        const outcome = outcomeOf(refusalReport);
        refusalReportsByOutcome.set(outcome, [...(refusalReportsByOutcome.get(outcome) ?? []), refusalReport]);
    }
    refusalReportsByOutcome.forEach((refusalReportsAlike) => sendCommentInsertRefusal(refusalReportsAlike[0], refusalReportsAlike));
}

function reportCommentInsertRefusal(report: CommentInsertRefusalReport): void {
    if (refusalReportsOfTheRun !== null) {
        refusalReportsOfTheRun.push(report);
        return;
    }
    sendCommentInsertRefusal(report, null);
}

function sendCommentInsertRefusal(report: CommentInsertRefusalReport, refusalReportsAlikeOfTheRun: CommentInsertRefusalReport[] | null): void {
    const outcome = outcomeOf(report);
    reportMessage(COMMENT_INSERT_REFUSED_MESSAGE, {
        level: report.landedRoute === null ? "error" : "warning",
        fingerprint: [COMMENT_INSERT_REFUSED_ERROR_TYPE, outcome],
        tags: {
            [SENTRY_TAG_ERROR_TYPE]: COMMENT_INSERT_REFUSED_ERROR_TYPE,
            [SENTRY_TAG_COMMENT_INSERT_OUTCOME]: outcome,
            [SENTRY_TAG_DOCUMENT_PAGE_COUNT]: pageCountTagOf(report.documentSize),
        },
        contexts: refusalReportsAlikeOfTheRun === null
            ? { [COMMENT_INSERT_REFUSAL_CONTEXT_KEY]: { ...report } }
            : {
                [COMMENT_INSERT_REFUSAL_CONTEXT_KEY]: { ...report },
                [COMMENT_INSERT_REFUSALS_OF_THE_RUN_CONTEXT_KEY]: { refusalCount: refusalReportsAlikeOfTheRun.length, refusals: refusalReportsAlikeOfTheRun },
            },
    });
}
